import React, { useEffect, useState } from "react";
import { pausableInterval } from "@/lib/appVisibility";
import { Loader2, WifiOff, Wifi } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { isOffline } from "@/lib/netStatus";

// Bandeau discret sous l'en-tête : appareil hors ligne ou serveur injoignable (session connue → l'app
// reste consultable : dernier fil lu). Disparaît 2,5 s après « Connexion rétablie ».
export const OfflineBanner = () => {
  const { user, reconnecting, reconnectNow, t } = useApp();
  const [offline, setOffline] = useState(isOffline());
  const [restored, setRestored] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const sync = () => setOffline(isOffline());
    const events = ["online", "offline", "yalola:network-online"];
    events.forEach((e) => window.addEventListener(e, sync));
    const stop = pausableInterval(sync, 3000); // lot 699 — plus de sonde toutes les 3 s quand l'app est cachée
    return () => { events.forEach((e) => window.removeEventListener(e, sync)); stop(); };
  }, []);

  const active = !!user && (offline || reconnecting);
  const [wasActive, setWasActive] = useState(active);
  useEffect(() => {
    if (active) { setWasActive(true); setRestored(false); return undefined; }
    if (!wasActive) return undefined;
    setRestored(true);
    const tm = setTimeout(() => { setRestored(false); setWasActive(false); }, 2500);
    return () => clearTimeout(tm);
  }, [active, wasActive]);

  if (!active && !restored) return null;
  const retry = async () => { setBusy(true); try { await reconnectNow(); } finally { setTimeout(() => setBusy(false), 1500); } };
  const tone = restored ? { bg: "#E8F5EC", fg: "#1F6B3A" } : offline ? { bg: "#FDF3E7", fg: "#8A4B1F" } : { bg: "#EEF3F8", fg: "#2F5D8A" };
  return (
    <div
      className="w-full px-3 py-1.5 text-[12.5px] font-semibold flex items-center justify-center gap-2 transition-colors"
      style={{ background: tone.bg, color: tone.fg }}
      role="status"
      data-testid="offline-banner"
      data-state={restored ? "restored" : offline ? "offline" : "reconnecting"}
    >
      {restored ? <Wifi size={14} /> : offline ? <WifiOff size={14} /> : <Loader2 size={14} className="animate-spin" />}
      <span>
        {restored
          ? t("offline.restored", "Connexion rétablie")
          : offline
            ? t("offline.banner", "Hors ligne · dernier contenu affiché")
            : t("offline.unstable", "Connexion instable — on réessaie…")}
      </span>
      {!restored && !offline && (
        <button type="button" onClick={retry} disabled={busy} className="underline underline-offset-2 disabled:opacity-60" data-testid="offline-banner-retry">
          {t("offline.retry", "Réessayer")}
        </button>
      )}
    </div>
  );
};
